/**
 * The console's HTTP client.
 *
 * <p>Holds the staff access token in memory only. The refresh token goes to sessionStorage so a
 * page reload does not force a new sign-in, but nothing survives closing the tab: this console can
 * lock and wipe laptops, and a token sitting in localStorage on a shared workstation is not a
 * trade worth making for convenience.
 */

const REFRESH_KEY = 'sentinel.refresh';

export interface ApiErrorBody {
  status: number;
  code: string;
  message: string;
  fieldErrors?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Page<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
let refreshInFlight: Promise<void> | null = null;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export function setSession(tokens: { accessToken: string; refreshToken: string }): void {
  accessToken = tokens.accessToken;
  sessionStorage.setItem(REFRESH_KEY, tokens.refreshToken);
}

export function clearSession(): void {
  accessToken = null;
  sessionStorage.removeItem(REFRESH_KEY);
}

export function hasStoredSession(): boolean {
  return sessionStorage.getItem(REFRESH_KEY) !== null;
}

async function refreshSession(): Promise<void> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new ApiError(401, 'no_session', 'Not signed in');

  const response = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    clearSession();
    throw new ApiError(401, 'session_expired', 'Your session has expired. Please sign in again.');
  }

  const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
  setSession(tokens);
}

async function ensureSession(): Promise<void> {
  if (accessToken) return;
  // One refresh at a time: a dashboard fires several requests at once on load, and each of them
  // finding no token must not start its own rotation (the server revokes a reused refresh token).
  if (!refreshInFlight) {
    refreshInFlight = refreshSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const anonymous = path.startsWith('/api/v1/auth/login') || path.startsWith('/api/v1/auth/refresh');
  if (!anonymous) await ensureSession();

  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && !anonymous ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401 && !anonymous && !isRetry) {
    accessToken = null;
    try {
      await ensureSession();
      return call<T>(method, path, body, true);
    } catch {
      onUnauthenticated?.();
      throw new ApiError(401, 'session_expired', 'Your session has expired. Please sign in again.');
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!response.ok) {
    let parsed: Partial<ApiErrorBody> = {};
    try {
      parsed = JSON.parse(text) as ApiErrorBody;
    } catch {
      // Not a structured error body; fall through to the raw text.
    }
    throw new ApiError(
      response.status,
      parsed.code ?? 'error',
      parsed.message ?? text.slice(0, 300) ?? 'Request failed',
    );
  }

  return text ? (JSON.parse(text) as T) : (undefined as T);
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined | null>) =>
    call<T>('GET', path + (params ? query(params) : '')),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body),
  delete: <T>(path: string) => call<T>('DELETE', path),
};
